<script setup lang="ts">
import { AppTypography } from '@/components';
import type { StoreFilterCategory, StoreProductFilters, StoreStockFilter } from '@/composables/useStoreProducts';

type PriceRange = [number, number];

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    categories: StoreFilterCategory[];
    filters: StoreProductFilters;
    priceBounds?: PriceRange;
    stockCounts?: Record<StoreStockFilter, number>;
    showCategories?: boolean;
    isMobile?: boolean;
  }>(),
  {
    priceBounds: () => [0, 0],
    stockCounts: () => ({ in_stock: 0, out_of_stock: 0 }),
    showCategories: true,
    isMobile: false,
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'apply', value: StoreProductFilters): void;
  (e: 'reset'): void;
}>();

const draftCategoryId = ref<number | null>(props.filters.categoryId ?? null);
const draftSubcategoryId = ref<number | null>(props.filters.subcategoryId ?? null);
const getInitialPriceRange = (): PriceRange => {
  const range = props.filters.priceRange ?? props.priceBounds;
  return [range[0], range[1]];
};

const draftPriceRange = ref<PriceRange>(getInitialPriceRange());
const draftStock = ref(props.filters.stock ?? null);
const scrollLockState = ref<{
  bodyOverflow: string;
  bodyOverscrollBehavior: string;
  bodyPosition: string;
  bodyTop: string;
  bodyWidth: string;
  htmlOverflow: string;
  htmlOverscrollBehavior: string;
  scrollY: number;
} | null>(null);

const selectedCategory = computed(() => {
  return props.categories.find((category) => category.id === draftCategoryId.value) ?? null;
});

const subcategories = computed(() => {
  return selectedCategory.value?.subcategories ?? [];
});

const categoryItems = computed(() => {
  return props.categories.map((category) => ({
    ...category,
    title: `${category.label} (${category.product_count})`,
    props: { disabled: category.product_count === 0 },
  }));
});

const subcategoryItems = computed(() => {
  return subcategories.value.map((subcategory) => ({
    ...subcategory,
    title: `${subcategory.label} (${subcategory.product_count})`,
    props: { disabled: subcategory.product_count === 0 },
  }));
});

const hasSubcategories = computed(() => subcategories.value.length > 0);
const isPriceDisabled = computed(() => props.priceBounds[0] === 0 && props.priceBounds[1] === 0);

const formatPrice = (price: number) => {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(price);
};

const hasCustomPriceRange = computed(() => {
  return draftPriceRange.value[0] !== props.priceBounds[0] || draftPriceRange.value[1] !== props.priceBounds[1];
});

const syncDraftFilters = () => {
  draftCategoryId.value = props.filters.categoryId ?? null;
  draftSubcategoryId.value = props.filters.subcategoryId ?? null;
  draftPriceRange.value = getInitialPriceRange();
  draftStock.value = props.filters.stock ?? null;
};

const close = () => {
  emit('update:modelValue', false);
};

const applyFilters = () => {
  emit('apply', {
    categoryId: draftCategoryId.value,
    subcategoryId: draftSubcategoryId.value,
    priceRange: hasCustomPriceRange.value ? draftPriceRange.value : null,
    stock: draftStock.value,
  });
  close();
};

const resetFilters = () => {
  draftCategoryId.value = null;
  draftSubcategoryId.value = null;
  draftPriceRange.value = [props.priceBounds[0], props.priceBounds[1]];
  draftStock.value = null;
  emit('reset');
  close();
};

const setBodyScrollLock = (isLocked: boolean) => {
  if (!import.meta.client) return;

  const { body, documentElement } = document;

  if (isLocked && scrollLockState.value === null) {
    const scrollY = window.scrollY;
    scrollLockState.value = {
      bodyOverflow: body.style.overflow,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      htmlOverflow: documentElement.style.overflow,
      htmlOverscrollBehavior: documentElement.style.overscrollBehavior,
      scrollY,
    };

    documentElement.style.overflow = 'hidden';
    documentElement.style.overscrollBehavior = 'none';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    return;
  }

  if (!isLocked && scrollLockState.value !== null) {
    const previousState = scrollLockState.value;
    documentElement.style.overflow = previousState.htmlOverflow;
    documentElement.style.overscrollBehavior = previousState.htmlOverscrollBehavior;
    body.style.overflow = previousState.bodyOverflow;
    body.style.overscrollBehavior = previousState.bodyOverscrollBehavior;
    body.style.position = previousState.bodyPosition;
    body.style.top = previousState.bodyTop;
    body.style.width = previousState.bodyWidth;
    window.scrollTo(0, previousState.scrollY);
    scrollLockState.value = null;
  }
};

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      syncDraftFilters();
    }
    setBodyScrollLock(isOpen);
  },
  { immediate: true }
);

watch(draftCategoryId, () => {
  if (!hasSubcategories.value || !subcategories.value.some((subcategory) => subcategory.id === draftSubcategoryId.value)) {
    draftSubcategoryId.value = null;
  }
});

onBeforeUnmount(() => {
  setBodyScrollLock(false);
});
</script>

<template>
  <v-navigation-drawer
    :model-value="modelValue"
    temporary
    location="left"
    :width="isMobile ? 320 : 380"
    class="store-product-filters"
    @update:model-value="(value) => emit('update:modelValue', value)"
  >
    <div class="store-product-filters__content">
      <div class="store-product-filters__header d-flex align-center justify-space-between">
        <AppTypography type="title-3">Filters</AppTypography>
        <v-btn icon="mdi-close" variant="text" size="small" @click="close" />
      </div>

      <div v-if="showCategories" class="store-product-filters__section">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="store-product-filters__section-title">
            <v-icon size="18" color="primary">mdi-currency-usd</v-icon>
            <AppTypography type="subheadline-1">Price range</AppTypography>
          </div>
          <AppTypography type="body-2" color="-secondary">
            ${{ formatPrice(draftPriceRange[0]) }} - ${{ formatPrice(draftPriceRange[1]) }}
          </AppTypography>
        </div>
        <v-range-slider
          v-model="draftPriceRange"
          :min="priceBounds[0]"
          :max="priceBounds[1]"
          :step="0.01"
          :disabled="isPriceDisabled"
          color="primary"
          hide-details
          thumb-label
        />
      </div>

      <div class="store-product-filters__section">
        <div class="store-product-filters__section-title mb-2">
          <v-icon size="18" color="primary">mdi-shape-outline</v-icon>
          <AppTypography type="subheadline-1">Category</AppTypography>
        </div>
        <v-select
          v-model="draftCategoryId"
          :items="categoryItems"
          item-title="title"
          item-value="id"
          placeholder="Select category"
          variant="outlined"
          density="comfortable"
          clearable
          hide-details
        />
      </div>

      <div v-if="showCategories && hasSubcategories" class="store-product-filters__section">
        <div class="store-product-filters__section-title mb-2">
          <v-icon size="18" color="primary">mdi-file-tree-outline</v-icon>
          <AppTypography type="subheadline-1">Sub-category</AppTypography>
        </div>
        <v-select
          v-model="draftSubcategoryId"
          :items="subcategoryItems"
          item-title="title"
          item-value="id"
          placeholder="Select sub-category"
          variant="outlined"
          density="comfortable"
          clearable
          hide-details
        />
      </div>

      <div class="store-product-filters__section">
        <div class="store-product-filters__section-title mb-2">
          <v-icon size="18" color="primary">mdi-package-variant-closed</v-icon>
          <AppTypography type="subheadline-1">Stock</AppTypography>
        </div>
        <v-radio-group v-model="draftStock" hide-details>
          <v-radio label="Any availability" :value="null" />
          <v-radio
            :label="`In Stock (${stockCounts.in_stock})`"
            value="in_stock"
            :disabled="stockCounts.in_stock === 0"
          />
          <v-radio
            :label="`Out of Stock (${stockCounts.out_of_stock})`"
            value="out_of_stock"
            :disabled="stockCounts.out_of_stock === 0"
          />
        </v-radio-group>
      </div>
    </div>

    <template #append>
      <div class="store-product-filters__footer d-flex ga-3">
        <v-btn variant="outlined" rounded class="flex-grow-1" @click="resetFilters">Reset</v-btn>
        <v-btn color="primary" rounded class="flex-grow-1" @click="applyFilters">Apply</v-btn>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
.store-product-filters {
  height: calc(100dvh - var(--v-layout-top, 0px)) !important;
  max-height: calc(100dvh - var(--v-layout-top, 0px)) !important;

  :deep(.v-navigation-drawer__content) {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .store-product-filters__content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 24px;
  }

  .store-product-filters__header {
    margin-bottom: 24px;
  }

  .store-product-filters__section {
    margin-bottom: 28px;
  }

  .store-product-filters__section-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .store-product-filters__footer {
    flex-shrink: 0;
    padding: 16px 24px calc(24px + env(safe-area-inset-bottom));
    border-top: 1px solid rgb(var(--v-theme-stroke));
    background: rgb(var(--v-theme-surface));
  }
}
</style>
